import type { HitArea } from './HitArea';
import { type Point, point } from './point';
import type { Polygon } from './polygon';
import { type Segment, segment } from './segment';
import type { NonZeroVector } from './vector';

function getRectangleClippingPointVertical(dY: number, height: number) {
  if (dY > 0) {
    return point.create(0, height / 2);
  }
  if (dY < 0) {
    return point.create(0, -height / 2);
  }
  throw new Error('Zero vector, types were probably deceived');
}

function getRectangleClippingPointHorizontal(dX: number, width: number) {
  if (dX > 0) {
    return point.create(width / 2, 0);
  }
  if (dX < 0) {
    return point.create(-width / 2, 0);
  }
  throw new Error('Zero vector, types were probably deceived');
}

/**
 * Get rectangle clipping point, assuming rectangle center at [0, 0]
 */
export function getRectangleClippingPoint(
  width: number,
  height: number,
  direction: NonZeroVector,
): Point {
  const { x: dX, y: dY } = direction;
  const dirSlope = dY / dX;
  const nodeSlope =
    width === 0 ? height * Number.POSITIVE_INFINITY : height / width;

  // Case: Vertical direction
  if (dX === 0) {
    return getRectangleClippingPointVertical(dY, height);
  }

  // Case: Horizontal direction
  if (dY === 0) {
    return getRectangleClippingPointHorizontal(dX, width);
  }

  // Case: Intersects the right border
  if (dX > 0 && dirSlope >= -nodeSlope && dirSlope <= nodeSlope) {
    return point.create(width / 2, (width * dY) / 2 / dX);
  }

  // Case: Intersects the left border
  if (dX < 0 && dirSlope >= -nodeSlope && dirSlope <= nodeSlope) {
    return point.create(-width / 2, -(width * dY) / 2 / dX);
  }

  // Case: Intersects the top border
  if (dY > 0 && (dirSlope <= -nodeSlope || dirSlope >= nodeSlope)) {
    return point.create((height * dX) / 2 / dY, height / 2);
  }

  // Case: Intersects the bottom border
  return point.create(-(height * dX) / 2 / dY, -height / 2);
}

/**
 * Works in SVG coordinates
 */
export class Rectangle {
  public topLeft: Point;
  public readonly width: number;
  public readonly height: number;

  constructor(topLeft: Point, width: number, height: number) {
    this.topLeft = topLeft;
    this.width = width;
    this.height = height;
  }

  get minX(): number {
    return this.topLeft.x;
  }

  get minY(): number {
    return this.topLeft.y;
  }

  get maxX(): number {
    return this.topLeft.x + this.width;
  }

  get maxY(): number {
    return this.topLeft.y + this.height;
  }

  get centreX(): number {
    return this.topLeft.x + this.width / 2;
  }

  get centreY(): number {
    return this.topLeft.y + this.height / 2;
  }

  get centre(): Point {
    return point.create(this.centreX, this.centreY);
  }

  get topRight(): Point {
    return point.create(this.maxX, this.minY);
  }

  get bottomRight(): Point {
    return point.create(this.maxX, this.maxY);
  }

  get bottomLeft(): Point {
    return point.create(this.minX, this.maxY);
  }

  get top(): Segment {
    return segment.create(this.topLeft, this.topRight);
  }

  get right(): Segment {
    return segment.create(this.topRight, this.bottomRight);
  }

  get bottom(): Segment {
    return segment.create(this.bottomRight, this.bottomLeft);
  }

  get left(): Segment {
    return segment.create(this.bottomLeft, this.topLeft);
  }

  public containsPoint(p: Point): boolean {
    return (
      p.x >= this.minX &&
      p.x <= this.maxX &&
      p.y >= this.minY &&
      p.y <= this.maxY
    );
  }

  /**
   * Get the intersection point from the centre in the direction of the provided vector
   */
  public getClippingPoint(direction: NonZeroVector): Point {
    return point.createOnVector(
      getRectangleClippingPoint(this.width, this.height, direction),
      this.centre,
    );
  }
}

export const getRectangleHitArea = (
  width: number,
  height: number,
): HitArea => ({
  contains: (x, y) =>
    x >= -width / 2 && y >= -height / 2 && x <= width / 2 && y <= height / 2,
});

export const createPointsBoundingBox = (...points: Point[]): Rectangle => {
  if (points.length === 0) {
    throw new Error('No points provided');
  }

  const xList = points.map((p) => p.x);
  const yList = points.map((p) => p.y);

  const minX = Math.min(...xList);
  const maxX = Math.max(...xList);
  const minY = Math.min(...yList);
  const maxY = Math.max(...yList);

  return new Rectangle(point.create(minX, minY), maxX - minX, maxY - minY);
};

export const mergeBoundingBoxes = (...rectangles: Rectangle[]): Rectangle => {
  if (rectangles.length === 0) {
    throw new Error('No input provided');
  }

  const minX = Math.min(...rectangles.map((r) => r.minX));
  const maxX = Math.max(...rectangles.map((r) => r.maxX));
  const minY = Math.min(...rectangles.map((r) => r.minY));
  const maxY = Math.max(...rectangles.map((r) => r.maxY));

  return new Rectangle(point.create(minX, minY), maxX - minX, maxY - minY);
};

export const getRotatedRectangle = (
  rectangle: Rectangle,
  rotationCenter: Point,
  radians: number,
): Polygon => [
  point.rotate(rectangle.topLeft, rotationCenter, radians),
  point.rotate(rectangle.topRight, rotationCenter, radians),
  point.rotate(rectangle.bottomRight, rotationCenter, radians),
  point.rotate(rectangle.bottomLeft, rotationCenter, radians),
];

/**
 * Resize rectangle by given dimensions, keeping its center in place
 */
export const resizeFromCenter = (
  input: Rectangle,
  deltaX: number,
  deltaY = deltaX,
): Rectangle =>
  new Rectangle(
    point.create(input.minX - deltaX, input.minY - deltaY),
    input.width + 2 * deltaX,
    input.height + 2 * deltaY,
  );

export const rectangle = {
  createPointsBoundingBox,
  getRotatedRectangle,
  mergeBoundingBoxes,
  resizeFromCenter,
};
